import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { SEO } from "../components/SEO";
import { stats } from "../data/mock";
import { api } from "../lib/api";
import {
  ArrowRight, Star, Target, Eye, Heart, Lightbulb,
  Award, Globe, Users, CheckCircle2
} from "lucide-react";

export default function AboutPage() {
  const [teamMembers, setTeamMembers] = useState([]);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    api.getTeam().then(setTeamMembers).catch(console.error);
    api.getTestimonials().then(setTestimonials).catch(console.error);
  }, []);
  const values = [
    { icon: Target, title: "Integrity", description: "We maintain the highest ethical standards in all our dealings, providing honest assessments and transparent processes." },
    { icon: Heart, title: "Client First", description: "Your success is our priority. We go above and beyond to ensure the best possible outcome for every client." },
    { icon: Lightbulb, title: "Innovation", description: "We stay updated with the latest immigration policies and leverage technology for efficient service delivery." },
    { icon: Award, title: "Excellence", description: "Our commitment to excellence is reflected in our 98% success rate and thousands of satisfied clients." }
  ];

  return (
    <div>
      <SEO 
        title="About Us"
        description="Learn about NexFLY Immigration Consultancy - your trusted partner for immigration services since 2022. Meet our expert team and discover our mission to make global dreams accessible."
        keywords="about NexFLY, immigration consultancy team, Coimbatore visa consultant, immigration experts India"
      />
      {/* Hero */}
      <section className="relative py-20 lg:py-28 bg-white overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-[50%] h-full bg-brand-50/50 rounded-br-[100px]" />
          <div className="absolute bottom-10 right-20 w-56 h-56 bg-brand-100/30 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <Badge className="bg-brand-100 text-brand-700 hover:bg-brand-100 mb-6 px-4 py-1.5 text-xs font-semibold rounded-full">
              About NexFLY
            </Badge>
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 leading-tight mb-6">
              Building Bridges to
              <span className="text-brand-500 block">Global Dreams</span>
            </h1>
            <p className="text-lg text-gray-500 leading-relaxed max-w-lg">
              Since 2022, NexFLY Immigration Consultancy has been helping individuals and families realize their dream of living, working, and studying abroad.
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24 bg-gray-50/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="border-0 shadow-sm bg-white rounded-2xl hover:shadow-lg transition-shadow duration-300">
              <CardContent className="p-10">
                <div className="w-14 h-14 bg-brand-100 rounded-2xl flex items-center justify-center mb-6">
                  <Target className="w-7 h-7 text-brand-500" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Mission</h3>
                <p className="text-gray-500 leading-relaxed">
                  To provide world-class immigration consultancy services that empower individuals and families to achieve their international aspirations. We are committed to delivering personalized, transparent, and result-oriented solutions that make global mobility accessible to everyone.
                </p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm bg-white rounded-2xl hover:shadow-lg transition-shadow duration-300">
              <CardContent className="p-10">
                <div className="w-14 h-14 bg-brand-100 rounded-2xl flex items-center justify-center mb-6">
                  <Eye className="w-7 h-7 text-brand-500" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Vision</h3>
                <p className="text-gray-500 leading-relaxed">
                  To be the most trusted and innovative immigration consultancy firm globally, known for our ethical practices, client satisfaction, and contribution to building a more connected world where borders don't limit human potential.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <Badge className="bg-brand-100 text-brand-700 hover:bg-brand-100 mb-4 px-4 py-1 text-xs font-semibold rounded-full">
              Our Values
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              The Principles That Guide Us
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <Card key={index} className="border-0 shadow-sm bg-white rounded-2xl hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group">
                <CardContent className="p-7 text-center">
                  <div className="w-14 h-14 bg-brand-100 rounded-2xl flex items-center justify-center mx-auto mb-5 group-hover:bg-brand-500 transition-colors duration-300">
                    <value.icon className="w-7 h-7 text-brand-500 group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="font-semibold text-gray-900 text-lg mb-2">{value.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-24 bg-brand-50/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <Badge className="bg-brand-100 text-brand-700 hover:bg-brand-100 mb-4 px-4 py-1 text-xs font-semibold rounded-full">
              Our Team
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Meet Our Expert Consultants
            </h2>
            <p className="text-gray-500 text-lg">
              A dedicated team of licensed professionals committed to your success.
            </p>
          </div>
          <div className="flex justify-center gap-8 flex-wrap">
            {teamMembers.map((member, index) => (
              <Card key={index} className="border-0 shadow-sm bg-white rounded-2xl hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group w-full sm:w-[320px]">
                <CardContent className="p-7 text-center">
                  <div className="w-20 h-20 bg-brand-100 rounded-2xl flex items-center justify-center mx-auto mb-5 group-hover:bg-brand-500 transition-colors duration-300">
                    <span className="text-2xl font-bold text-brand-500 group-hover:text-white transition-colors duration-300">{member.initials}</span>
                  </div>
                  <h3 className="font-semibold text-gray-900 text-lg mb-1">{member.name}</h3>
                  <p className="text-brand-500 text-sm font-medium mb-3">{member.role}</p>
                  <p className="text-gray-500 text-sm leading-relaxed">{member.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-brand-800 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-3xl sm:text-4xl font-bold text-white mb-1">{stat.value}</p>
                <p className="text-brand-200 text-sm font-medium">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <Badge className="bg-brand-100 text-brand-700 hover:bg-brand-100 mb-4 px-4 py-1 text-xs font-semibold rounded-full">
              Testimonials
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Client Success Stories
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.slice(0, 3).map((testimonial, index) => (
              <Card key={index} className="border-0 shadow-sm bg-gray-50 rounded-2xl">
                <CardContent className="p-7">
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-amber-400 fill-amber-400" />
                    ))}
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed mb-6 italic">
                    "{testimonial.content}"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-brand-100 rounded-full flex items-center justify-center">
                      <span className="text-sm font-bold text-brand-500">{testimonial.name.charAt(0)}</span>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{testimonial.name}</p>
                      <p className="text-xs text-gray-500">{testimonial.role}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gray-50/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-brand-800 rounded-3xl p-12 lg:p-16 text-center relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-brand-700/30 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-brand-900/30 rounded-full blur-3xl" />
            <div className="relative">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Let's Build Your Future Together
              </h2>
              <p className="text-brand-200 text-lg mb-8 max-w-xl mx-auto">
                Take the first step towards your international dream. Our experts are ready to guide you.
              </p>
              <Link to="/contact">
                <Button className="bg-white text-brand-700 hover:bg-brand-50 rounded-xl h-12 px-8 text-sm font-semibold shadow-lg transition-all duration-300 hover:-translate-y-0.5">
                  Get Started Today
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
